import { useState, useCallback } from 'react';
import { useApi } from '../../../shared/hooks/useApi';
import { chatAPI, type Message, type Conversation, type SendMessageRequest } from '../../../services/chatApi';

export function useChat() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  
  const {
    execute: sendMessageRequest,
    isLoading: isSending,
    error: sendError
  } = useApi<Message>(chatAPI.sendMessage);

  const {
    execute: loadConversationsRequest,
    isLoading: isLoadingConversations,
    error: conversationsError
  } = useApi<Conversation[]>(chatAPI.getConversations);

  const {
    execute: loadMessagesRequest,
    isLoading: isLoadingMessages,
    error: messagesError
  } = useApi<any>(chatAPI.getConversationMessages);

  const {
    execute: markAsReadRequest,
    isLoading: isMarkingRead,
    error: markReadError
  } = useApi<void>(chatAPI.markMessagesAsRead);

  const loadConversations = useCallback(async () => {
    const result = await loadConversationsRequest();
    if (result) {
      setConversations(result);
    }
    return result;
  }, [loadConversationsRequest]);

  const loadMessages = useCallback(async (conversationId: string) => {
    const result = await loadMessagesRequest(conversationId);
    if (result) {
      // Messages come in DESC order (newest first), reverse for display
      const messagesInOrder = [...result.content].reverse();
      setMessages(prev => ({
        ...prev,
        [conversationId]: messagesInOrder
      }));
    }
    return result;
  }, [loadMessagesRequest]);

  const sendMessage = useCallback(async (recipientId: string, content: string) => {
    const result = await sendMessageRequest({ recipientId, content });
    if (result) {
      // Add the new message to the conversation
      const conversationId = result.conversationId;
      setMessages(prev => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), result]
      }));
      
      // Refresh conversations to update last message
      loadConversations();
    }
    return result;
  }, [sendMessageRequest, loadConversations]);

  const markMessagesAsRead = useCallback(async (conversationId: string) => {
    const result = await markAsReadRequest(conversationId);
    if (result !== undefined) {
      // Update local messages to mark as read
      setMessages(prev => ({
        ...prev,
        [conversationId]: prev[conversationId]?.map(msg => ({
          ...msg,
          isRead: true,
          readAt: new Date().toISOString()
        })) || []
      }));
      
      // Refresh conversations to update unread count
      loadConversations();
    }
    return result;
  }, [markAsReadRequest, loadConversations]);

  const findOrCreateConversation = useCallback((friendId: string, friendName: string) => {
    // Look for existing conversation with this friend
    const existingConversation = conversations.find(conv => 
      conv.otherParticipant.id === friendId
    );
    
    if (existingConversation) {
      return existingConversation;
    }
    
    // Return a placeholder conversation that will be created when first message is sent
    return {
      id: '', // Will be set when first message is sent
      participant1Id: '',
      participant2Id: friendId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      unreadCount: 0,
      otherParticipant: {
        id: friendId,
        username: friendName.toLowerCase().replace(/\s+/g, ''),
        firstName: friendName.split(' ')[0],
        lastName: friendName.split(' ').slice(1).join(' '),
        fullName: friendName,
      }
    };
  }, [conversations]);

  const addMessageToConversation = useCallback((conversationId: string, message: Message) => {
    setMessages(prev => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), message]
    }));
  }, []);

  return {
    conversations,
    messages,
    isLoading: isLoadingConversations || isLoadingMessages,
    isSending,
    isMarkingRead,
    error: conversationsError || messagesError || sendError || markReadError,
    loadConversations,
    loadMessages,
    sendMessage,
    markMessagesAsRead,
    findOrCreateConversation,
    addMessageToConversation
  };
}